import type { ComponentProps, FC, ReactNode } from 'react'

import Link from 'next/link'
import { ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'

export type ButtonVariant = 'primary' | 'secondary'

interface ButtonLinkProps extends ComponentProps<typeof Link> {
    variant?: ButtonVariant
    withArrow?: boolean
}

const base = 'inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-control px-4 py-3 text-base leading-[1.3] font-medium transition-[box-shadow,opacity] duration-300 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:cursor-not-allowed disabled:opacity-60'

const variants: Record<ButtonVariant, string> = {
    primary: 'bg-primary bg-linear-to-r from-primary to-brand-violet text-primary-foreground shadow-button hover:shadow-button-hover',
    secondary: 'bg-background bg-linear-to-r from-brand-mist to-transparent text-foreground shadow-outline hover:shadow-outline-hover'
}

// Shared look for every call-to-action, also used on real <button> elements in forms.
export const buttonClasses = (variant: ButtonVariant = 'primary', className?: string): string =>
    cn(base, variants[variant], className)

export const ButtonLink: FC<ButtonLinkProps> = ({
    variant = 'primary',
    withArrow = true,
    className,
    children,
    ...props
}): ReactNode => {
    return (
        <Link className={buttonClasses(variant, className)} {...props}>
            <span>
                {children}
            </span>

            {withArrow && <ArrowRight className='size-4' strokeWidth={1.75} aria-hidden='true' />}
        </Link>
    )
}
